import { apiClient } from '../utils/api';
import { UrlBuilder } from '../utils/urls';
import { TransactionEntity, TransactionEntityFetchFilter } from '../models';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: number;
}

/** Request body for POST /api/transaction-entities/create/{user_id} */
export interface CreateTransactionEntityData {
  transaction_entity_name: string;
  transaction_entity_description?: string;
  transaction_entity_category: 'action' | 'object' | 'indirect_object' | 'media';
  transaction_entity_type?: 'credit' | 'debit' | 'tradable' | 'non_tradable' | 'payable' | 'third_party' | string | null;
  transaction_entity_past_tense?: string | null;
  linked_to_kpi?: boolean;
  position?: number;
}

/** Response from POST /api/transaction-entities/create/{user_id} */
export interface CreateTransactionEntityApiResponse {
  message: string;
  entity: {
    transaction_entity_name: string;
    transaction_entity_description: string | null;
    transaction_entity_category: string;
    transaction_entity_type: string | null;
    transaction_entity_vector: number[];
    transaction_entity_past_tense: string | null;
    id: number;
  };
}

export interface UpdateTransactionEntityData {
  transaction_entity_name?: string;
  transaction_entity_description?: string;
  transaction_entity_category?: string;
  transaction_entity_type?: string | null;
  transaction_entity_past_tense?: string | null;
  linked_to_kpi?: boolean;
  record_id?: number;
  position?: number;
}

/** Request body for POST /api/transaction-entities/find/{user_id} */
export interface FindTransactionEntitiesBody {
  id?: number;
  name?: string;
  category?: string;
  type?: string;
  linked_to_kpi?: boolean;
  limit?: number;
  page?: number;
  record_id?: number;
}

/** Single entity in find API response (snake_case) */
export interface TransactionEntityApiItem {
  id: number;
  transaction_entity_name: string;
  transaction_entity_description: string | null;
  transaction_entity_category: string;
  transaction_entity_type: string | null;
  transaction_entity_vector?: string | number[];
  transaction_entity_past_tense: string | null;
  is_linked_to_kpi?: boolean;
  position?: number;
}

/** Paginated response from POST /api/transaction-entities/find/{user_id} */
export interface FindTransactionEntitiesApiResponse {
  current_page: number;
  data: TransactionEntityApiItem[];
  first_page_url: string;
  from: number | null;
  last_page: number;
  last_page_url: string;
  links: Array<{ url: string | null; label: string; active: boolean }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
}

export function mapApiEntityToTransactionEntity(item: TransactionEntityApiItem): TransactionEntity {
  return new TransactionEntity({
    id: item.id,
    transaction_entity_name: item.transaction_entity_name,
    transaction_entity_description: item.transaction_entity_description ?? undefined,
    transaction_entity_category: item.transaction_entity_category,
    transaction_entity_type: item.transaction_entity_type ?? undefined,
    transaction_entity_past_tense: item.transaction_entity_past_tense ?? undefined,
    linked_to_kpi: item.is_linked_to_kpi ?? false,
    position: item.position,
  });
}

/** Single item in similarity search response */
export interface SimilaritySearchEntityApi {
  id: number;
  transaction_entity_name: string;
  transaction_entity_description: string | null;
  transaction_entity_category: string;
  transaction_entity_type: string | null;
  transaction_entity_past_tense: string | null;
  is_exclusive?: number;
}

/** Response item from POST /api/transaction-entities/find-by-cosine-similarity/{user_id} */
export interface SimilaritySearchResult {
  entity: SimilaritySearchEntityApi;
  similarity: number;
}

class TransactionEntitiesService {
  // Find transaction entities for a user (POST with body, paginated)
  async findTransactionEntities(
    token: string,
    userId: number,
    body?: FindTransactionEntitiesBody
  ): Promise<ApiResponse<FindTransactionEntitiesApiResponse>> {
    try {
      const payload: Record<string, unknown> = {};
      if (body) {
        if (body.id !== undefined) payload.id = body.id;
        if (body.name !== undefined && body.name !== '') payload.name = body.name;
        if (body.category !== undefined && body.category !== '') payload.category = body.category;
        if (body.type !== undefined && body.type !== '') payload.type = body.type;
        if (body.linked_to_kpi !== undefined) payload.linked_to_kpi = body.linked_to_kpi;
        if (body.limit !== undefined) payload.limit = body.limit;
        if (body.page !== undefined) payload.page = body.page;
        if (body.record_id !== undefined) payload.record_id = body.record_id;
      }

      const url = UrlBuilder.transactionEntities.find(userId);
      const response = await apiClient.postWithToken<FindTransactionEntitiesApiResponse>(url, token, payload);

      if (response.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }

  /** @deprecated Use findTransactionEntities (POST) instead. Kept for backward compatibility. */
  async getTransactionEntities(token: string, userId: number, filters?: TransactionEntityFetchFilter): Promise<ApiResponse<TransactionEntity[]>> {
    try {
      const body: FindTransactionEntitiesBody = {};
      if (filters) {
        if (filters.id !== undefined) body.id = filters.id;
        if (filters.name) body.name = filters.name;
        if (filters.category) body.category = filters.category;
        if (filters.type) body.type = filters.type;
        if (filters.linked_to_kpi !== undefined) body.linked_to_kpi = filters.linked_to_kpi;
        if (filters.limit !== undefined) body.limit = filters.limit;
        if (filters.page !== undefined) body.page = filters.page;
      }
      const result = await this.findTransactionEntities(token, userId, body);
      if (!result.success || !result.data) {
        return { success: false, error: result.error, code: result.code };
      }
      const entities = (result.data.data ?? []).map(mapApiEntityToTransactionEntity);
      return { success: true, data: entities };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }

  // Find transaction entities by cosine similarity with token
  async findByCosineSimilarity(
    token: string,
    userId: number,
    query: string,
    category?: 'action' | 'object' | 'indirect_object' | 'media',
    limit?: number
  ): Promise<ApiResponse<SimilaritySearchResult[]>> {
    try {
      const body: Record<string, unknown> = { query };
      if (category) body.category = category;
      if (limit !== undefined) body.limit = limit;

      const response = await apiClient.postWithToken<SimilaritySearchResult[]>(
        UrlBuilder.transactionEntities.findByCosineSimilarity(userId),
        token,
        body
      );

      if (response.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }

  // Create a new transaction entity with token
  async createTransactionEntity(
    token: string,
    userId: number,
    data: CreateTransactionEntityData
  ): Promise<ApiResponse<CreateTransactionEntityApiResponse>> {
    try {
      const response = await apiClient.postWithToken<CreateTransactionEntityApiResponse>(
        UrlBuilder.transactionEntities.create(userId),
        token,
        data
      );

      if (response.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }

  // Update a transaction entity with token
  async updateTransactionEntity(token: string, id: number, data: UpdateTransactionEntityData): Promise<ApiResponse<TransactionEntity>> {
    try {
      const response = await apiClient.putWithToken<TransactionEntity>(
        UrlBuilder.transactionEntities.edit(id),
        token,
        data
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Delete a transaction entity with token
  async deleteTransactionEntity(token: string, id: number): Promise<ApiResponse> {
    try {
      const response = await apiClient.deleteWithToken(
        UrlBuilder.transactionEntities.delete(id),
        token
      );

      if (response.success) {
        return {
          success: true
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }
}

export const transactionEntitiesService = new TransactionEntitiesService();